import { Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  ContentActionBarMainItem,
  SiContentActionBarComponent
} from '@siemens/element-ng/content-action-bar';
import { MenuItem } from '@siemens/element-ng/menu';

import { RouteEditorService } from '../../services/route-editor.service';

/**
 * Map actions, replacing the legacy `L.easyBar` button strip.
 *
 * Labels stay as keys: the action bar translates them itself, which also keeps them
 * reactive to language changes.
 *
 * Keyboard shortcuts are handled by the host component so they keep working when focus
 * is inside the map canvas rather than on a toolbar button.
 */
@Component({
  selector: 'app-map-toolbar',
  imports: [SiContentActionBarComponent, TranslateModule],
  templateUrl: './map-toolbar.component.html'
})
export class MapToolbarComponent {
  readonly beelineEnabled = input(false);
  readonly drawEnabled = input(true);
  readonly elevationEnabled = input(false);
  readonly dataTableEnabled = input(false);
  readonly distanceMarkersEnabled = input(false);
  readonly routeQualityEnabled = input(false);
  readonly readonlyMode = input(false);

  readonly toggleDraw = output<void>();
  readonly toggleBeeline = output<void>();
  readonly toggleElevation = output<void>();
  readonly toggleDataTable = output<void>();
  readonly toggleDistanceMarkers = output<void>();
  readonly toggleRouteQuality = output<void>();
  readonly exportFile = output<void>();
  readonly importFile = output<void>();
  readonly reverseRoute = output<void>();
  readonly deleteLastPoint = output<void>();
  readonly clearRoute = output<void>();
  readonly exportToUi = output<void>();
  readonly editRoute = output<void>();

  private readonly routeEditor = inject(RouteEditorService);

  readonly waypointCount = computed(() => this.routeEditor.waypoints().length);
  readonly hasWaypoints = computed(() => this.waypointCount() > 0);
  /** The engine needs at least a start and a destination before a track exists. */
  readonly canExport = computed(() => this.waypointCount() >= 2 && !this.routeEditor.isRouting());
  readonly canInspectTrack = computed(
    () => this.waypointCount() >= 2 && !this.routeEditor.isRouting()
  );

  readonly primaryActions = computed<ContentActionBarMainItem[]>(() => {
    if (this.readonlyMode()) {
      return [
        {
          type: 'action',
          label: 'SHARED.EDIT',
          icon: 'element-edit',
          iconOnly: true,
          action: () => this.editRoute.emit()
        },
        {
          type: 'checkbox',
          label: 'BROUTER_MAP.TOGGLE_ELEVATION',
          icon: 'element-trend',
          iconOnly: true,
          checked: this.elevationEnabled(),
          disabled: !this.canInspectTrack(),
          action: () => this.toggleElevation.emit()
        },
        {
          type: 'checkbox',
          label: 'BROUTER_MAP.TOGGLE_DATA',
          icon: 'element-list',
          iconOnly: true,
          checked: this.dataTableEnabled(),
          disabled: !this.canInspectTrack(),
          action: () => this.toggleDataTable.emit()
        }
      ];
    }

    return [
      {
        type: 'checkbox',
        label: 'BROUTER_MAP.TOGGLE_DRAW',
        icon: 'element-edit',
        iconOnly: true,
        checked: this.drawEnabled(),
        action: () => this.toggleDraw.emit()
      },
      {
        type: 'action',
        label: 'BROUTER_MAP.REVERSE_ROUTE',
        icon: 'element-repeat',
        iconOnly: true,
        disabled: this.waypointCount() < 2,
        action: () => this.reverseRoute.emit()
      },
      {
        type: 'action',
        label: 'BROUTER_MAP.CLEAR_ROUTE',
        icon: 'element-delete',
        iconOnly: true,
        disabled: !this.hasWaypoints(),
        action: () => this.clearRoute.emit()
      }
    ];
  });

  readonly secondaryActions = computed<MenuItem[]>(() => {
    const inspectionActions: MenuItem[] = [
      { type: 'header', label: 'BROUTER_MAP.TOOLBAR' },
      {
        type: 'checkbox',
        label: 'BROUTER_MAP.TOGGLE_ELEVATION',
        icon: 'element-trend',
        checked: this.elevationEnabled(),
        disabled: !this.canInspectTrack(),
        action: () => this.toggleElevation.emit()
      },
      {
        type: 'checkbox',
        label: 'BROUTER_MAP.TOGGLE_DATA',
        icon: 'element-list',
        checked: this.dataTableEnabled(),
        disabled: !this.canInspectTrack(),
        action: () => this.toggleDataTable.emit()
      },
      {
        type: 'checkbox',
        label: 'BROUTER_MAP.TOGGLE_QUALITY',
        icon: 'element-palette',
        checked: this.routeQualityEnabled(),
        disabled: !this.canInspectTrack(),
        action: () => this.toggleRouteQuality.emit()
      },
      {
        type: 'checkbox',
        label: 'BROUTER_MAP.TOGGLE_DISTANCE_MARKERS',
        icon: 'element-ruler',
        checked: this.distanceMarkersEnabled(),
        disabled: !this.canInspectTrack(),
        action: () => this.toggleDistanceMarkers.emit()
      }
    ];

    if (this.readonlyMode()) {
      return inspectionActions;
    }

    return [
      { type: 'header', label: 'BROUTER_MAP.MENU.FILE' },
      {
        type: 'action',
        label: 'BROUTER_MAP.LOAD.TITLE',
        icon: 'element-import',
        action: () => this.importFile.emit()
      },
      {
        type: 'action',
        label: 'BROUTER_MAP.EXPORT.TITLE',
        icon: 'element-export',
        disabled: !this.canExport(),
        action: () => this.exportFile.emit()
      },
      { type: 'divider' },
      { type: 'header', label: 'BROUTER_MAP.MENU.ROUTE' },
      {
        type: 'checkbox',
        label: 'BROUTER_MAP.TOGGLE_BEELINE',
        icon: 'element-interpolation-linear',
        checked: this.beelineEnabled(),
        action: () => this.toggleBeeline.emit()
      },
      {
        type: 'action',
        label: 'BROUTER_MAP.DELETE_LAST_POINT',
        icon: 'element-undo',
        disabled: !this.hasWaypoints(),
        action: () => this.deleteLastPoint.emit()
      },
      {
        type: 'action',
        label: 'BROUTER_MAP.CLEAR_ROUTE',
        icon: 'element-delete',
        disabled: !this.hasWaypoints(),
        action: () => this.clearRoute.emit()
      },
      { type: 'divider' },
      ...inspectionActions
    ];
  });
}
